import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAudioPlayerOptions {
  sampleRate?: number;
  onPlayStateChange?: (messageId?: string) => void;
}

interface PlayAudioTask {
  messageId: string;
  audioData?: string;
  message?: string;
  onSynthesize?: (messageId: string, text: string) => void;
}

interface UseAudioPlayerReturn {
  // 状态
  currentPlayingId: string | null;
  loadingId: string | null; // 正在加载音频的消息ID
  queueLength: number; // 队列长度

  // 播放方法
  playPcm: (
    messageId: string,
    base64Audio: string,
    options?: { streaming?: boolean; sampleRate?: number },
  ) => void;
  playEncodedAudio: (messageId: string, base64Audio: string) => void;
  playAudio: (
    messageId: string,
    audioData?: string,
    message?: string,
    onSynthesize?: (messageId: string, text: string) => void,
    interrupt?: boolean,
  ) => void;

  // 控制方法
  stop: () => void;

  // 缓存管理
  getCachedAudio: (messageId: string) => string | undefined;
  setCachedAudio: (messageId: string, audioData: string) => void;
  clearCache: () => void;
}

/**
 * 音频播放器 Hook
 * 支持 PCM 流式播放、PCM 完整播放、编码格式播放
 */
export const useAudioPlayer = (
  options: UseAudioPlayerOptions = {},
): UseAudioPlayerReturn => {
  const { sampleRate = 24000, onPlayStateChange } = options;

  // ==================== Refs ====================
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioQueueRef = useRef<AudioBufferSourceNode[]>([]);
  const nextStartTimeRef = useRef<number>(0);
  const htmlAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioCache = useRef<Map<string, string>>(new Map());
  const playQueueRef = useRef<PlayAudioTask[]>([]); // 播放队列
  const processQueueRef = useRef<(() => void) | null>(null); // 处理队列的函数引用
  const playingIdRef = useRef('');

  // ==================== State ====================
  const [currentPlayingId, setCurrentPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [queueLength, setQueueLength] = useState<number>(0); // 队列长度

  // ==================== 播放状态变化通知 ====================
  useEffect(() => {
    onPlayStateChange?.(currentPlayingId || undefined);
  }, [currentPlayingId, onPlayStateChange]);

  // ==================== 获取或创建 AudioContext ====================
  const getAudioContext = useCallback(
    (rate: number = sampleRate) => {
      if (
        !audioContextRef.current ||
        audioContextRef.current.state === 'closed'
      ) {
        audioContextRef.current = new (
          window.AudioContext ||
          ('webkitAudioContext' in window && (window as any).webkitAudioContext)
        )({
          sampleRate: rate,
        });
        nextStartTimeRef.current = audioContextRef.current.currentTime;
      }
      return audioContextRef.current;
    },
    [sampleRate],
  );

  // ==================== PCM 数据转换为 Float32 ====================
  const pcmToFloat32 = useCallback((audioData: Uint8Array): Float32Array => {
    const floatArray = new Float32Array(audioData.length / 2);

    for (let i = 0; i < audioData.length; i += 2) {
      // 读取小端字节序的16位整数
      const sample = (audioData[i + 1] << 8) | audioData[i];
      // 转换为有符号整数（-32768 到 32767）
      const signedSample = sample > 32767 ? sample - 65536 : sample;
      // 归一化到 [-1, 1] 范围
      floatArray[i / 2] = signedSample / 32768.0;
    }

    return floatArray;
  }, []);

  // ==================== 停止播放 ====================
  const stop = useCallback(() => {
    // 停止 HTML Audio 元素
    if (htmlAudioRef.current) {
      htmlAudioRef.current.pause();
      htmlAudioRef.current = null;
    }

    // 停止所有 Web Audio API 的音频源
    audioQueueRef.current.forEach((source) => {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // 忽略已经停止的错误
      }
    });
    audioQueueRef.current = [];

    // 重置下次播放时间
    if (audioContextRef.current) {
      nextStartTimeRef.current = audioContextRef.current.currentTime;
    }

    setLoadingId(null);
    setCurrentPlayingId(null);
    playingIdRef.current = '';
    // 不清空播放队列，只停止当前播放
    // playQueueRef.current = [];
    // setQueueLength(0);
  }, []);

  // ==================== 播放 PCM 音频（内部方法，支持队列）====================
  const playPcmInternal = useCallback(
    (
      messageId: string,
      base64Audio: string,
      options: { streaming?: boolean; sampleRate?: number } = {},
    ) => {
      const { streaming = false, sampleRate: rate = sampleRate } = options;

      try {
        // 解码 base64
        const bytes = Uint8Array.from(atob(base64Audio), (c) =>
          c.charCodeAt(0),
        );

        const audioContext = getAudioContext(rate);
        const buffer = audioContext.createBuffer(1, bytes.length / 2, rate);
        const channelData = buffer.getChannelData(0);

        // 转换 PCM 数据
        const floatData = pcmToFloat32(bytes);
        channelData.set(floatData);

        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContext.destination);

        // 流式播放：确保音频块按顺序播放，不重叠
        // 完整播放：立即播放
        const startTime = streaming
          ? Math.max(nextStartTimeRef.current, audioContext.currentTime)
          : 0;

        // 开始播放，清除 loading 状态
        setLoadingId(null);
        setCurrentPlayingId(messageId);
        playingIdRef.current = messageId;

        source.start(startTime);

        // 更新下一个音频块的开始时间（仅流式播放）
        if (streaming) {
          nextStartTimeRef.current = startTime + buffer.duration;
        }

        // 保存到队列中
        audioQueueRef.current.push(source);

        // 清理已完成的音频源
        source.onended = () => {
          const index = audioQueueRef.current.indexOf(source);
          if (index > -1) {
            audioQueueRef.current.splice(index, 1);
          }
          // 如果队列为空，更新播放状态并处理播放队列
          if (audioQueueRef.current.length === 0) {
            setCurrentPlayingId(null);
            playingIdRef.current = '';
            processQueueRef.current?.();
          }
        };
      } catch (error) {
        console.error('[AudioPlayer] PCM playback failed:', error);
        setCurrentPlayingId(null);
        playingIdRef.current = '';
        processQueueRef.current?.();
      }
    },
    [sampleRate, getAudioContext, pcmToFloat32],
  );

  // ==================== 播放编码格式音频（MP3 等）内部方法 ====================
  const playEncodedAudioInternal = useCallback(
    (messageId: string, base64Audio: string) => {
      try {
        // 创建音频元素
        const audio = new Audio();
        htmlAudioRef.current = audio;

        audio.src = base64Audio;
        audio.onplay = () => {
          // 开始播放，清除 loading 状态
          setLoadingId(null);
          setCurrentPlayingId(messageId);
          playingIdRef.current = messageId;
        };
        audio.onended = () => {
          setCurrentPlayingId(null);
          playingIdRef.current = '';
          // 播放完成后处理队列
          processQueueRef.current?.();
        };
        audio.onerror = (e) => {
          setCurrentPlayingId(null);
          playingIdRef.current = '';
          console.error('[AudioPlayer] Encoded audio playing error:', e);
          // 错误时也处理队列
          processQueueRef.current?.();
        };

        audio.play().catch((err) => {
          console.error('[AudioPlayer] Audio autoplay failed:', err);
          setCurrentPlayingId(null);
          playingIdRef.current = '';
          // 播放失败时也处理队列
          processQueueRef.current?.();
        });
      } catch (error) {
        console.error('[AudioPlayer] Encoded audio playback failed:', error);
        setCurrentPlayingId(null);
        playingIdRef.current = '';
        // 异常时也处理队列
        processQueueRef.current?.();
      }
    },
    [],
  );

  // ==================== 处理播放队列 ====================
  const processQueue = useCallback(() => {
    if (playQueueRef.current.length === 0) {
      return;
    }

    const nextTask = playQueueRef.current.shift();
    setQueueLength(playQueueRef.current.length);

    if (nextTask) {
      const { messageId, audioData, message, onSynthesize } = nextTask;

      // 如果有音频数据，直接播放
      if (audioData) {
        const isEncodedAudio = audioData.startsWith('data:audio/');
        if (isEncodedAudio) {
          playEncodedAudioInternal(messageId, audioData);
        } else {
          playPcmInternal(messageId, audioData);
        }
      }
      // 否则触发合成
      else if (message && onSynthesize) {
        try {
          const content = JSON.parse(message);
          if (typeof content !== 'object') {
            setLoadingId(messageId);
            onSynthesize(messageId, message);
          }
        } catch (error) {
          setLoadingId(messageId);
          onSynthesize(messageId, message);
        }
      }
    }
  }, [playEncodedAudioInternal, playPcmInternal]);

  // 将 processQueue 赋值给 ref，供其他函数使用
  processQueueRef.current = processQueue;

  // ==================== 播放 PCM 音频（支持流式和完整播放）====================
  const playPcm = useCallback(
    (
      messageId: string,
      base64Audio: string,
      options: { streaming?: boolean; sampleRate?: number } = {},
    ) => {
      const { streaming = false } = options;

      if (!streaming) {
        stop();
      }
      playPcmInternal(messageId, base64Audio, options);
    },
    [playPcmInternal, stop],
  );

  // ==================== 播放编码格式音频（MP3 等）====================
  const playEncodedAudio = useCallback(
    (messageId: string, base64Audio: string) => {
      stop();
      playEncodedAudioInternal(messageId, base64Audio);
    },
    [playEncodedAudioInternal, stop],
  );

  // ==================== 通用播放方法（支持队列和打断）====================
  const playAudio = useCallback(
    (
      messageId: string,
      audioData?: string,
      message?: string,
      onSynthesize?: (messageId: string, text: string) => void,
      interrupt: boolean = false,
    ) => {
      // console.log(222222, messageId, playingIdRef.current, message);

      if (!audioData && message) {
        try {
          const content = JSON.parse(message);
          if (typeof content === 'object') {
            return;
          }
        } catch (error) {}
      }
      // 如果允许打断，或者当前没有播放，立即播放
      if (interrupt || !playingIdRef.current) {
        // 停止当前播放并清空队列
        if (interrupt) {
          stop();
          playQueueRef.current = [];
          setQueueLength(0);
        }
        playingIdRef.current = messageId;

        // 如果有音频数据，直接播放
        if (audioData) {
          const isEncodedAudio = audioData.startsWith('data:audio/');
          if (isEncodedAudio) {
            playEncodedAudioInternal(messageId, audioData);
          } else {
            playPcmInternal(messageId, audioData);
          }
        }
        // 否则触发合成
        else if (message && onSynthesize) {
          setLoadingId(messageId);
          onSynthesize(messageId, message);
        }
      } else {
        // 正在播放且不打断，加入队列
        playQueueRef.current.push({
          messageId,
          audioData,
          message,
          onSynthesize,
        });
        setQueueLength(playQueueRef.current.length);
        console.log(
          `[AudioPlayer] Added to queue. Queue length: ${playQueueRef.current.length}`,
        );
      }
    },
    [currentPlayingId, playEncodedAudioInternal, playPcmInternal, stop],
  );

  // ==================== 缓存管理 ====================
  const getCachedAudio = useCallback((messageId: string) => {
    return audioCache.current.get(messageId);
  }, []);

  const setCachedAudio = useCallback((messageId: string, audioData: string) => {
    audioCache.current.set(messageId, audioData);
  }, []);

  const clearCache = useCallback(() => {
    audioCache.current.clear();
  }, []);

  // ==================== 清理资源 ====================
  useEffect(() => {
    return () => {
      // 停止所有音频
      stop();
      // 关闭 AudioContext
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, [stop]);

  return {
    // 状态
    currentPlayingId,
    loadingId,
    queueLength,

    // 播放方法
    playPcm,
    playEncodedAudio,
    playAudio,

    // 控制方法
    stop,

    // 缓存管理
    getCachedAudio,
    setCachedAudio,
    clearCache,
  };
};

export default useAudioPlayer;
